import { getOnlineStatus } from '@airship-platform/airship-patterns';
import { type HttpClient } from '@airship-platform/http-service';
import { type RxCollection } from 'rxdb';
import { replicateRxCollection } from 'rxdb/plugins/replication';

type PushedDocument = {
  status?: string;
  syncedAt?: number;
  updatedAt?: string;
  offlineSyncErrors?: unknown;
};

const patchRowsUnchangedSincePush = async <RxDocType extends PushedDocument>(
  collection: RxCollection<RxDocType>,
  snapshots: RxDocType[],
  fields: Partial<PushedDocument>
) => {
  if (!snapshots.length) {
    return;
  }
  const primaryPath = collection.schema.primaryPath as keyof RxDocType;
  const docs = await collection
    .findByIds(snapshots.map((d) => String(d[primaryPath])))
    .exec();

  await Promise.all(
    snapshots.map(async (snapshot) => {
      const doc = docs.get(String(snapshot[primaryPath]));
      if (!doc) {
        return;
      }
      await doc.incrementalModify((current) =>
        current.syncedAt === 0 && current.updatedAt === snapshot.updatedAt
          ? ({ ...current, ...fields } as RxDocType)
          : current
      );
    })
  );
};

/**
 * Shared push-only replication for the appointment-proposal entity collections.
 * Every proposal collection ships local writes (those still marked
 * `syncedAt: 0`) to `/appointments/sync/push` identically — only the
 * collection and replication identifier differ.
 */
export const setupEntityPush = <RxDocType extends PushedDocument>(
  collection: RxCollection<RxDocType>,
  httpClient: HttpClient,
  replicationIdentifier: string
) => {
  const replication = replicateRxCollection<RxDocType, { syncedAt: number }>({
    replicationIdentifier,
    collection,
    push: {
      handler: async (rows) => {
        const filteredRows = rows.filter(
          (r) => r.newDocumentState.syncedAt === 0
        );
        if (!filteredRows.length) {
          return [];
        }

        const conflicts = await httpClient.post<RxDocType[]>(
          `/appointments/sync/push`,
          filteredRows
        );
        const syncedAt = Date.now();
        const primaryPath = collection.schema.primaryPath as keyof RxDocType;
        const conflictIds = new Set(conflicts.map((d) => d[primaryPath]));
        const accepted = filteredRows
          .map((r) => r.newDocumentState)
          .filter((d) => !conflictIds.has(d[primaryPath]));

        await patchRowsUnchangedSincePush(collection, accepted, {
          syncedAt,
          offlineSyncErrors: null,
        });

        return conflicts.map((d) => ({
          ...d,
          syncedAt,
          _deleted: !d.offlineSyncErrors && d.status === 'DELETED',
        }));
      },
      batchSize: 50,
    },
    live: true,
    waitForLeadership: true,
    retryTime: 5_000,
  });

  if (!getOnlineStatus()) {
    replication.pause();
  }

  let onlineTimer: ReturnType<typeof setTimeout> | null = null;
  const clearOnlineTimer = () => {
    if (onlineTimer) {
      clearTimeout(onlineTimer);
      onlineTimer = null;
    }
  };

  const onOffline = () => {
    clearOnlineTimer();
    replication.pause();
  };
  const onOnline = () => {
    clearOnlineTimer();
    onlineTimer = setTimeout(() => replication.start(), 1000);
  };
  window.addEventListener('offline', onOffline);
  window.addEventListener('online', onOnline);

  const originalCancel = replication.cancel.bind(replication);
  replication.cancel = () => {
    window.removeEventListener('offline', onOffline);
    window.removeEventListener('online', onOnline);
    clearOnlineTimer();
    return originalCancel();
  };

  return replication;
};
